/* Encryption for the sync blob — §6.
 *
 * The passphrase never leaves the browser. What goes to the Worker is AES-GCM
 * ciphertext plus the salt and IV needed to decrypt it, which are useless
 * without the passphrase. There is no recovery path: if the passphrase is lost
 * the blob is lost, which is why the plaintext export is the real backup. */

const ITERATIONS = 250_000;
const KEY_BITS = 256;
const SALT_BYTES = 16;
const IV_BYTES = 12;   // 96 bits, the size AES-GCM is specified for

export interface Envelope {
  ciphertext: string;  // base64
  salt: string;        // base64
  iv: string;          // base64
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: ITERATIONS, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** A fresh salt and IV on every write. Reusing an IV under one key is the single
    way to break AES-GCM, so neither is ever carried over. */
export async function encrypt(plaintext: string, passphrase: string): Promise<Envelope> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const key = await deriveKey(passphrase, salt);
  const buffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    new TextEncoder().encode(plaintext),
  );
  return {
    ciphertext: toBase64(new Uint8Array(buffer)),
    salt: toBase64(salt),
    iv: toBase64(iv),
  };
}

export class WrongPassphrase extends Error {
  constructor() {
    super('That passphrase does not open this copy.');
    this.name = 'WrongPassphrase';
  }
}

/** A wrong passphrase fails the GCM authentication tag and throws. It cannot
    return partial or corrupted plaintext — that is the point of the tag. */
export async function decrypt(envelope: Envelope, passphrase: string): Promise<string> {
  const key = await deriveKey(passphrase, fromBase64(envelope.salt));
  try {
    const buffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64(envelope.iv) as BufferSource },
      key,
      fromBase64(envelope.ciphertext) as BufferSource,
    );
    return new TextDecoder().decode(buffer);
  } catch {
    throw new WrongPassphrase();
  }
}
